export const PAYMENT_CURRENCY_STORAGE_KEY = 'jlastore.payment-currency.v3';
export const PAYMENT_CURRENCY_CHANGED_EVENT = 'jlastore:payment-currency-changed';
export const DEFAULT_USD_TO_KHR_RATE = 4100;
export const USD_TO_KHR_RATE = DEFAULT_USD_TO_KHR_RATE;
export const PAYMENT_CURRENCIES = ['USD', 'KHR'];
export const DEFAULT_PAYMENT_CURRENCY = 'USD';

const RATE_STORAGE_KEY = 'jlastore.exchange-rate.khr';
const SETTINGS_STORAGE_KEY = 'jlastore.currency-settings.v1';
let memorySettings;
let memoryRate = DEFAULT_USD_TO_KHR_RATE;
let memoryChoice;
const FALLBACK_CURRENCIES = [
  { currency_code: 'USD', currency_symbol: '$', rate_to_usd: 1, status: 1 },
  { currency_code: 'KHR', currency_symbol: '៛', rate_to_usd: DEFAULT_USD_TO_KHR_RATE, status: 1 },
];

export const readCurrencySettings = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(SETTINGS_STORAGE_KEY));
    if (saved?.currencies?.length) return saved;
  } catch (error) { if (memorySettings) return memorySettings; }
  return { currencies: FALLBACK_CURRENCIES, default_currency: DEFAULT_PAYMENT_CURRENCY };
};

const notifyCurrencyChange = () => {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(PAYMENT_CURRENCY_CHANGED_EVENT, {
    detail: { currency: readPaymentCurrency() },
  }));
};

export const applyCurrencySettings = (settings) => {
  const currencies = (Array.isArray(settings?.currencies) ? settings.currencies : []).filter(currency =>
    Number(currency.status) === 1 && /^[A-Z]{3,10}$/.test(currency.currency_code)
      && Number.isFinite(Number(currency.rate_to_usd)) && Number(currency.rate_to_usd) > 0);
  if (!currencies.length) return;
  const defaultCode = currencies.find(currency => currency.currency_code === settings.default_currency)?.currency_code
    || currencies.find(currency => Number(currency.is_default) === 1)?.currency_code || currencies[0].currency_code;
  const next = JSON.stringify({ currencies, default_currency: defaultCode });
  const previous = JSON.stringify(readCurrencySettings());
  memorySettings = { currencies, default_currency: defaultCode };
  try { window.localStorage.setItem(SETTINGS_STORAGE_KEY, next); } catch (error) {}
  const oldRate = getUsdToKhrRate();
  const rate = setUsdToKhrRate(settings.usd_to_khr || currencies.find(currency => currency.currency_code === 'KHR')?.rate_to_usd);
  if (next !== previous || rate !== oldRate) notifyCurrencyChange();
};

export const normalizeDisplayCurrency = (value) => {
  const settings = readCurrencySettings();
  const currency = String(value || '').trim().toUpperCase();
  return settings.currencies.some(item => item.currency_code === currency) ? currency : settings.default_currency;
};

export const getUsdToKhrRate = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_USD_TO_KHR_RATE;
  }
  try {
    const stored = window.localStorage.getItem(RATE_STORAGE_KEY);
    const parsed = Number(stored);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_USD_TO_KHR_RATE;
  } catch (e) {
    return memoryRate;
  }
};

export const setUsdToKhrRate = (rate) => {
  const numericRate = Number(rate);
  if (Number.isFinite(numericRate) && numericRate > 0) {
    memoryRate = numericRate;
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(RATE_STORAGE_KEY, String(numericRate));
      } catch (e) {}
    }
    return numericRate;
  }
  return DEFAULT_USD_TO_KHR_RATE;
};

export const normalizePaymentCurrency = (value) => {
  const currency = String(value || '').trim().toUpperCase();
  return PAYMENT_CURRENCIES.includes(currency) ? currency : DEFAULT_PAYMENT_CURRENCY;
};

export const readPaymentCurrency = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_PAYMENT_CURRENCY;
  }

  try {
    const stored = window.localStorage.getItem(PAYMENT_CURRENCY_STORAGE_KEY);
    return normalizeDisplayCurrency(stored);
  } catch (error) {
    return normalizeDisplayCurrency(memoryChoice);
  }
};

export const writePaymentCurrency = (value) => {
  const currency = normalizeDisplayCurrency(value);
  memoryChoice = currency;

  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(PAYMENT_CURRENCY_STORAGE_KEY, currency);
    } catch (error) {
      // Storage can fail in private mode; the caller still receives the normalized value.
    }
  }
  notifyCurrencyChange();

  return currency;
};

export const usdToKhr = (amountUsd, rate = getUsdToKhrRate()) => {
  const amount = Number.parseFloat(amountUsd);
  if (!Number.isFinite(amount)) {
    return 0;
  }
  const effectiveRate = Number(rate) > 0 ? Number(rate) : DEFAULT_USD_TO_KHR_RATE;
  return Math.round(amount * effectiveRate);
};

export const formatPaymentAmount = (amountUsd, currency = 'USD', customRate = null) => {
  const code = String(currency || '').trim().toUpperCase();
  const normalizedCurrency = PAYMENT_CURRENCIES.includes(code) ? code : normalizeDisplayCurrency(code);

  if (normalizedCurrency === 'KHR') {
    return `${usdToKhr(amountUsd, customRate || getUsdToKhrRate()).toLocaleString('en-US')}៛`;
  }

  const amount = Number.parseFloat(amountUsd);
  const numericAmount = Number.isFinite(amount) ? amount : 0;
  if (normalizedCurrency === 'USD') return `$${numericAmount.toFixed(2)}`;
  const definition = readCurrencySettings().currencies.find(item => item.currency_code === normalizedCurrency);
  const converted = numericAmount * Number(definition.rate_to_usd);
  return `${converted.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${definition.currency_symbol || normalizedCurrency}`;
};

export const formatPaymentCurrencyRate = (customRate = null) => {
  const rate = customRate || getUsdToKhrRate();
  return `1$ = ${Number(rate).toLocaleString('en-US')}៛`;
};
